import { Show, createMemo, createSignal } from "solid-js";
import { A, Navigate, useParams } from "@solidjs/router";
import { useApi } from "../../api/provider.tsx";
import { Async } from "../../components/Async.tsx";
import {
  Actions,
  Button,
  DownloadLink,
  FieldGroup,
  Form,
  SelectField,
} from "../../components/Form.tsx";
import { Panel } from "../../components/Panel.tsx";
import { Table } from "../../components/Table.tsx";
import { TreeSelect } from "../../components/TreeSelect.tsx";
import { deleteColumn } from "../../components/DeleteButton.tsx";
import { useI18n } from "../../i18n/provider.tsx";
import { createLoader, loadedValue } from "../../lib/async.ts";
import { useSession } from "../../session/provider.tsx";
import { formatMoment } from "../../lib/format.ts";
import { questionnaireStatus } from "../../api/domain.ts";
import type { Answer } from "../../api/domain.ts";
import type { Questionnaire } from "../../api/domain.ts";
import { historyHref } from "./history.ts";
import { scopeOf } from "./questionnaire-scope.ts";
import { statusLabel } from "./questionnaire-status.ts";
import styles from "./admin.module.scss";

const blankQuestionnaire = (): Questionnaire => ({
  id: "",
  author: "",
  product: null,
  active: true,
  created_at: Date.now(),
  received_at: null,
  answered_at: null,
  evaluated_at: null,
  questions: [],
  topics: [],
});

/** The magic link a recipient opens; the questionnaire's id is the token. */
export const magicLink = (origin: string, id: string): string => `${origin}/q/${id}`;

/**
 * Every questionnaire with its derived status, plus the create form. The screen
 * is the consultant's alone: a customer user, or a consultant previewing one,
 * is sent back to the start, because questionnaires are reached from the magic
 * link and never administered from inside the portal.
 */
export const QuestionnairesPage = () => {
  const session = useSession();
  return (
    <Show when={!session.isCustomer()} fallback={<Navigate href="/" />}>
      <AdminQuestionnairesPage />
    </Show>
  );
};

const AdminQuestionnairesPage = () => {
  const api = useApi();
  const i18n = useI18n();

  const questionnaires = createLoader(() => api.list("questionnaires"));
  const authors = createLoader(() => api.list("authors"));
  const tree = createLoader(() => api.tree({ lang: i18n.locale() }));
  const products = createLoader(() => api.list("products", { lang: i18n.locale() }));
  const [draft, setDraft] = createSignal<Questionnaire | undefined>(undefined);
  /** The scope as ticked: question ids, split into the stored lists on save. */
  const [scope, setScope] = createSignal<string[]>([]);

  const save = async (questionnaire: Questionnaire) => {
    const result = await api.create("questionnaires", {
      ...questionnaire,
      ...scopeOf(loadedValue(tree.value()) ?? [], scope()),
    });
    if (!result.ok) return;
    setDraft(undefined);
    setScope([]);
    questionnaires.reload();
  };

  return (
    <>
      <div class={styles.heading}>
        <h1>{i18n.t().questionnaires.title}</h1>
      </div>

      <Panel
        actions={
          <Button
            label={i18n.t().questionnaires.newQuestionnaire}
            onClick={() => setDraft(blankQuestionnaire())}
          />
        }
      >
        <Show when={draft()}>
          {(questionnaire) => (
            <Async value={authors.value()} onRetry={authors.reload}>
              {(rows) => (
                <Form>
                  <SelectField
                    label={i18n.t().common.author}
                    placeholder={i18n.t().common.none}
                    choices={rows.map((author) => ({ id: author.id, label: author.name }))}
                    value={questionnaire().author}
                    onPick={(author) => setDraft({ ...questionnaire(), author })}
                  />
                  {/*
                    The product is fixed here and never by the recipient: an
                    author must not learn which other products are assessed.
                  */}
                  <Async value={products.value()} onRetry={products.reload}>
                    {(list) => (
                      <SelectField
                        label={i18n.t().common.product}
                        hint={i18n.t().questionnaires.productHint}
                        placeholder={i18n.t().common.none}
                        choices={list
                          .filter((entry) => entry.active)
                          .map((entry) => ({ id: entry.id, label: entry.name }))}
                        value={questionnaire().product ?? ""}
                        onPick={(product) =>
                          setDraft({ ...questionnaire(), product: product === "" ? null : product })
                        }
                      />
                    )}
                  </Async>
                  {/* Without an author there is nothing to pick, and no hint of where to get one. */}
                  <Show when={rows.length === 0}>
                    <p class={styles.note}>
                      {i18n.t().questionnaires.noAuthors}{" "}
                      <A href="/authors">{i18n.t().questionnaires.addAuthor}</A>
                    </p>
                  </Show>
                  <FieldGroup
                    label={i18n.t().questionnaires.scope}
                    hint={i18n.t().questionnaires.scopeHint}
                  >
                    <Async value={tree.value()} onRetry={tree.reload}>
                      {(branches) => (
                        <TreeSelect
                          topics={branches}
                          selected={scope()}
                          onChange={setScope}
                          allLabel={i18n.t().tree.selectAll}
                        />
                      )}
                    </Async>
                  </FieldGroup>
                  <Actions>
                    <Button
                      label={i18n.t().common.create}
                      tone="primary"
                      disabled={
                        questionnaire().author === "" ||
                        questionnaire().product === null ||
                        scope().length === 0
                      }
                      onClick={() => void save(questionnaire())}
                    />
                    <Button label={i18n.t().common.cancel} onClick={() => setDraft(undefined)} />
                  </Actions>
                </Form>
              )}
            </Async>
          )}
        </Show>

        <Async value={questionnaires.value()} onRetry={questionnaires.reload}>
          {(rows) => (
            <Table
              rows={rows}
              rowKey={(questionnaire) => questionnaire.id}
              emptyLabel={i18n.t().common.empty}
              columns={[
                {
                  key: "id",
                  header: i18n.t().common.identity,
                  cell: (questionnaire) => (
                    <A href={`/questionnaires/${questionnaire.id}`}>{questionnaire.id}</A>
                  ),
                },
                {
                  key: "status",
                  header: i18n.t().common.status,
                  cell: (questionnaire) =>
                    statusLabel(i18n.t(), questionnaireStatus(questionnaire)),
                },
                {
                  key: "author",
                  header: i18n.t().common.author,
                  cell: (questionnaire) =>
                    (loadedValue(authors.value()) ?? []).find(
                      (author) => author.id === questionnaire.author,
                    )?.name ?? questionnaire.author,
                },
                {
                  key: "created",
                  header: i18n.t().questionnaires.created,
                  cell: (questionnaire) => formatMoment(questionnaire.created_at, i18n.locale()),
                },
                deleteColumn({
                  header: i18n.t().common.actions,
                  collection: "questionnaires",
                  id: (questionnaire) => questionnaire.id,
                  onRemoved: questionnaires.reload,
                }),
              ]}
            />
          )}
        </Async>
      </Panel>

    </>
  );
};

/**
 * One questionnaire: its link, its status and what came in.
 *
 * The staged answers come from the API-key-scoped review route, which leaves the
 * stamps untouched. Reading them through the magic link would mark the
 * questionnaire received, a state that belongs to the recipient alone. A
 * customer has no business here either, so they are sent back to the start.
 */
export const QuestionnairePage = () => {
  const session = useSession();
  return (
    <Show when={!session.isCustomer()} fallback={<Navigate href="/" />}>
      <AdminQuestionnairePage />
    </Show>
  );
};

const AdminQuestionnairePage = () => {
  const api = useApi();
  const i18n = useI18n();
  const params = useParams<{ id: string }>();

  const questionnaire = createLoader(() => api.get("questionnaires", params.id));
  const fill = createLoader(() => api.review(params.id, { lang: i18n.locale() }));
  const products = createLoader(() => api.list("products", { lang: i18n.locale() }));
  /**
   * The answers already attributed to this questionnaire. Drafts are only what a
   * magic-link recipient staged; an answer written straight through the MCP tool
   * surface never was a draft, so without this it would appear nowhere on the
   * page it belongs to.
   */
  const answers = createLoader(() => api.list("answers"));
  const [copied, setCopied] = createSignal(false);
  const [accepted, setAccepted] = createSignal(false);

  const mine = createMemo<Answer[]>(() =>
    (loadedValue(answers.value()) ?? [])
      .filter((answer) => answer.questionnaire === params.id)
      .sort((left, right) => right.valid_from - left.valid_from),
  );

  const link = () =>
    magicLink(typeof location === "undefined" ? "" : location.origin, params.id);

  const copy = async () => {
    await navigator.clipboard?.writeText(link());
    setCopied(true);
  };

  const accept = async () => {
    const result = await api.acceptQuestionnaire(params.id);
    if (!result.ok) return;
    setAccepted(true);
    questionnaire.reload();
    // Accepting consumes the drafts and writes answers, so both lists moved.
    fill.reload();
    answers.reload();
  };

  /**
   * Whether there is anything to accept. `answered_at` alone is not the test:
   * only a magic-link submit sets it, so a questionnaire filled in through MCP
   * would never become acceptable and its answers would stay out of every report.
   */
  const pending = () =>
    (loadedValue(fill.value())?.drafts.length ?? 0) > 0 || mine().length > 0;

  return (
    <>
      <Async value={questionnaire.value()} onRetry={questionnaire.reload}>
        {(entity) => (
          <>
            <div class={styles.heading}>
              <h1>{entity.id}</h1>
              <p class={styles.lede}>
                {statusLabel(i18n.t(), questionnaireStatus(entity))} ·{" "}
                {formatMoment(entity.created_at, i18n.locale())}
              </p>
              <A href={historyHref("questionnaire", entity.id)}>{i18n.t().history.link}</A>
            </div>

            <Panel title={i18n.t().questionnaires.magicLink}>
              <p class={styles.mono}>{link()}</p>
              <Actions>
                <Button
                  label={copied() ? i18n.t().common.copied : i18n.t().questionnaires.copyLink}
                  onClick={() => void copy()}
                />
                <Button
                  label={accepted() ? i18n.t().questionnaires.accepted : i18n.t().questionnaires.accept}
                  tone="primary"
                  disabled={!pending() || entity.evaluated_at !== null}
                  onClick={() => void accept()}
                />
              </Actions>
            </Panel>

            <Panel
              title={i18n.t().questionnaires.workbook}
              description={i18n.t().questionnaires.workbookHint}
            >
              {/*
                No product to pick: the questionnaire named one when it was
                created, and the workbook is written for exactly that one.
              */}
              <Async value={products.value()} onRetry={products.reload}>
                {(list) => (
                  <Show
                    when={entity.product !== null}
                    fallback={<p class={styles.note}>{i18n.t().questionnaires.noProduct}</p>}
                  >
                    <p class={styles.note}>
                      {i18n.t().common.product}:{" "}
                      {list.find((entry) => entry.id === entity.product)?.name ?? entity.product}
                    </p>
                    <Actions>
                      <DownloadLink
                        label={i18n.t().reports.downloadWorkbook}
                        href={api.workbookUrl("questionnaires", entity.id, {
                          lang: i18n.locale(),
                          product: entity.product ?? "",
                        })}
                        filename={`${entity.id}-${entity.product}-${i18n.locale()}.xlsx`}
                      />
                    </Actions>
                  </Show>
                )}
              </Async>
            </Panel>
          </>
        )}
      </Async>

      <Panel title={i18n.t().questionnaires.drafts} description={i18n.t().questionnaires.review}>
        <Async value={fill.value()} onRetry={fill.reload}>
          {(view) => (
            <Table
              rows={view.drafts}
              rowKey={(draft) => `${draft.product}:${draft.question}`}
              emptyLabel={i18n.t().questionnaires.noDrafts}
              columns={[
                {
                  key: "product",
                  header: i18n.t().common.product,
                  cell: (draft) =>
                    view.products.find((product) => product.id === draft.product)?.name ??
                    draft.product,
                },
                {
                  key: "question",
                  header: i18n.t().common.question,
                  cell: (draft) =>
                    view.questions.find((question) => question.id === draft.question)?.title ??
                    draft.question,
                },
                {
                  key: "value",
                  header: i18n.t().answer.value,
                  cell: (draft) =>
                    draft.value === null || draft.value === undefined
                      ? i18n.t().common.dontKnow
                      : String(
                          Array.isArray(draft.value.value)
                            ? draft.value.value.join(", ")
                            : draft.value.value,
                        ),
                },
                {
                  key: "comment",
                  header: i18n.t().fill.comment,
                  cell: (draft) => draft.comment ?? "",
                },
                {
                  key: "explanation",
                  header: i18n.t().fill.explanation,
                  cell: (draft) => draft.explanation ?? "",
                },
              ]}
            />
          )}
        </Async>
      </Panel>

      <Panel
        title={i18n.t().questionnaires.answers}
        description={i18n.t().questionnaires.answersHint}
      >
        <Async value={answers.value()} onRetry={answers.reload}>
          {() => (
            <Table
              rows={mine()}
              rowKey={(answer) => answer.id}
              emptyLabel={i18n.t().questionnaires.noAnswers}
              columns={[
                {
                  key: "question",
                  header: i18n.t().common.question,
                  cell: (answer) => answer.question,
                },
                {
                  key: "value",
                  header: i18n.t().answer.value,
                  cell: (answer) =>
                    answer.value === null || answer.value === undefined
                      ? i18n.t().common.dontKnow
                      : String(
                          Array.isArray(answer.value.value)
                            ? answer.value.value.join(", ")
                            : answer.value.value,
                        ),
                },
                {
                  key: "source",
                  header: i18n.t().common.author,
                  cell: (answer) =>
                    `${answer.author} (${
                      answer.source === "ai"
                        ? i18n.t().answer.sourceAi
                        : i18n.t().answer.sourceHuman
                    })`,
                },
                {
                  key: "valid_from",
                  header: i18n.t().answer.validFrom,
                  cell: (answer) => formatMoment(answer.valid_from, i18n.locale()),
                },
                {
                  key: "explanation",
                  header: i18n.t().fill.explanation,
                  cell: (answer) => answer.explanation ?? "",
                },
                deleteColumn({
                  header: i18n.t().common.actions,
                  collection: "answers",
                  id: (answer) => answer.id,
                  onRemoved: answers.reload,
                }),
              ]}
            />
          )}
        </Async>
      </Panel>
    </>
  );
};
